import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, ArrowRight, CheckCircle, X, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Transfer, TransferItem } from '@/types/location.types';
import { toast } from 'sonner';
import { authFetch } from '@/services/api.service';
import { useAuth } from '@/context/AuthContext';
import { DecimalKeypad } from '@/components/ui/keypads/DecimalKeypad';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Interface for API transfer response
interface ApiTransferDetail {
  id: number;
  transferNumber: string;
  fromLocationId: number;
  fromLocationName: string;
  toLocationId: number;
  toLocationName: string;
  status: 'pending' | 'approved' | 'in_transit' | 'received' | 'cancelled';
  type: 'distribution' | 'request' | 'transfer' | 'return';
  notes?: string;
  observations?: string;
  requestedBy?: string;
  approvedBy?: string;
  shippedBy?: string;
  receivedBy?: string;
  preparator?: {
    id: number;
    fullName: string;
    position?: string;
  };
  driver?: {
    id: number;
    fullName: string;
    licenseNumber: string;
    licenseType: string;
  };
  vehicle?: {
    id: number;
    plate: string;
    brand: string;
    model: string;
    color?: string;
  };
  transportCompany?: {
    id: number;
    companyName: string;
    ruc: string;
  };
  requestedAt?: string;
  approvedAt?: string;
  shippedAt?: string;
  receivedAt?: string;
  estimatedDelivery?: string;
  actualDelivery?: string;
  createdAt: string;
  updatedAt: string;
  items: Array<{
    id: number;
    ingredientId: number;
    ingredientName: string;
    unit: string;
    requestedQuantity: number;
    confirmedQuantity?: number;
    receivedQuantity?: number;
    reason?: string;
  }>;
}

// Map API response to frontend Transfer type
const mapApiToTransfer = (api: ApiTransferDetail): Transfer => ({
  id: String(api.id),
  transferNumber: api.transferNumber,
  fromLocationId: String(api.fromLocationId),
  fromLocation: {
    id: String(api.fromLocationId),
    name: api.fromLocationName,
    type: 'central',
    address: '',
    isActive: true,
    timezone: 'America/Lima',
    createdAt: '',
    updatedAt: ''
  },
  toLocationId: String(api.toLocationId),
  toLocation: {
    id: String(api.toLocationId),
    name: api.toLocationName,
    type: 'sucursal',
    address: '',
    isActive: true,
    timezone: 'America/Lima',
    createdAt: '',
    updatedAt: ''
  },
  status: api.status === 'approved' ? 'pending' : api.status,
  type: api.type,
  items: api.items.map(item => ({
    id: String(item.id),
    transferId: String(api.id),
    ingredientId: String(item.ingredientId),
    ingredient: {
      id: String(item.ingredientId),
      name: item.ingredientName,
      unit: item.unit,
      category: '',
      currentStock: 0,
      minStock: 0,
      maxStock: 0,
      costPerUnit: 0,
      supplierId: '',
      isActive: true,
      createdAt: '',
      updatedAt: ''
    },
    requestedQuantity: item.requestedQuantity,
    confirmedQuantity: item.confirmedQuantity,
    receivedQuantity: item.receivedQuantity,
    unit: item.unit,
    reason: item.reason
  })),
  notes: api.notes,
  requestedBy: api.requestedBy || '',
  approvedBy: api.approvedBy,
  shippedBy: api.shippedBy,
  receivedBy: api.receivedBy,
  requestedAt: api.requestedAt || api.createdAt,
  approvedAt: api.approvedAt,
  shippedAt: api.shippedAt,
  receivedAt: api.receivedAt,
  estimatedDelivery: api.estimatedDelivery,
  actualDelivery: api.actualDelivery
});

export const TransferDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCajero = user?.positionId === 3;

  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [transferShippingInfo, setTransferShippingInfo] = useState<{
    preparator?: ApiTransferDetail['preparator'];
    driver?: ApiTransferDetail['driver'];
    vehicle?: ApiTransferDetail['vehicle'];
    transportCompany?: ApiTransferDetail['transportCompany'];
    observations?: string;
    estimatedDelivery?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItems, setEditingItems] = useState<Record<string, any>>({});
  const [selectedPreparator, setSelectedPreparator] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Datos de combos para el modal de envío
  const [employees, setEmployees] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [transportCompanies, setTransportCompanies] = useState<any[]>([]);

  // Keypad state for cajero
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keypadItemId, setKeypadItemId] = useState<string | null>(null);
  const [keypadTitle, setKeypadTitle] = useState('');
  const [keypadInitialValue, setKeypadInitialValue] = useState('0');
  const [keypadFieldName, setKeypadFieldName] = useState<string>('receivedQuantity');

  const [shippingData, setShippingData] = useState({
    driverId: '',
    vehicleId: '',
    transportCompanyId: '',
    estimatedDeliveryDate: '',
    estimatedDeliveryTime: '',
    observations: ''
  });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    requiresPreparator?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    requiresPreparator: false
  });

  // Cargar datos de combos para el modal de envío
  useEffect(() => {
    const fetchComboData = async () => {
      try {
        const [employeesRes, driversRes, vehiclesRes, companiesRes] = await Promise.all([
          authFetch(`${API_BASE_URL}/employees?limit=100`),
          authFetch(`${API_BASE_URL}/drivers`),
          authFetch(`${API_BASE_URL}/vehicles`),
          authFetch(`${API_BASE_URL}/transport-companies`)
        ]);

        let loadedEmployees: any[] = [];
        if (employeesRes.ok) {
          const data = await employeesRes.json();
          loadedEmployees = data.data?.employees || [];
          setEmployees(loadedEmployees);
        }
        let loadedDrivers: any[] = [];
        let loadedVehicles: any[] = [];
        let loadedCompanies: any[] = [];

        if (driversRes.ok) {
          const data = await driversRes.json();
          loadedDrivers = data.data || [];
          setDrivers(loadedDrivers);
        }
        if (vehiclesRes.ok) {
          const data = await vehiclesRes.json();
          loadedVehicles = data.data || [];
          setVehicles(loadedVehicles);
        }
        if (companiesRes.ok) {
          const data = await companiesRes.json();
          loadedCompanies = data.data || [];
          setTransportCompanies(loadedCompanies);
        }

        // Auto-seleccionar valores aleatorios y fecha/hora por defecto
        const randomItem = (arr: any[]) => arr.length > 0 ? String(arr[Math.floor(Math.random() * arr.length)].id) : '';
        const now = new Date();
        const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
        const todayStr = now.toISOString().split('T')[0];
        const timeStr = twoHoursLater.toTimeString().slice(0, 5);

        setShippingData(prev => ({
          ...prev,
          driverId: prev.driverId || randomItem(loadedDrivers),
          vehicleId: prev.vehicleId || randomItem(loadedVehicles),
          transportCompanyId: prev.transportCompanyId || randomItem(loadedCompanies),
          estimatedDeliveryDate: prev.estimatedDeliveryDate || todayStr,
          estimatedDeliveryTime: prev.estimatedDeliveryTime || timeStr
        }));
        if (loadedEmployees.length > 0) {
          setSelectedPreparator(prev => prev || randomItem(loadedEmployees));
        }
      } catch (error) {
        console.error('Error fetching combo data:', error);
      }
    };

    fetchComboData();
  }, []);

  useEffect(() => {
    const fetchTransfer = async () => {
      try {
        setLoading(true);
        const response = await authFetch(`${API_BASE_URL}/transfers/${id}`);

        if (!response.ok) {
          throw new Error('Error fetching transfer');
        }

        const result = await response.json();
        if (result.success && result.data) {
          setTransfer(mapApiToTransfer(result.data));
          // Guardar datos de envío de la API
          setTransferShippingInfo({
            preparator: result.data.preparator,
            driver: result.data.driver,
            vehicle: result.data.vehicle,
            transportCompany: result.data.transportCompany,
            observations: result.data.observations,
            estimatedDelivery: result.data.estimatedDelivery
          });
        } else {
          setTransfer(null);
        }
      } catch (error) {
        console.error('Error fetching transfer:', error);
        setTransfer(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTransfer();
    }
  }, [id]);

  const getStatusBadge = (status: Transfer['status']) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_transit: 'bg-blue-100 text-blue-800',
      received: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    const labels = {
      pending: 'Pendiente',
      in_transit: 'En Tránsito',
      received: 'Recibido',
      cancelled: 'Cancelada'
    };

    return (
      <Badge className={styles[status]}>
        {labels[status]}
      </Badge>
    );
  };

  const getStatusIcon = (status: Transfer['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'in_transit':
        return <Truck className="w-5 h-5 text-blue-600" />;
      case 'received':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'cancelled':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
    }
  };

  const getTypeLabel = (type: Transfer['type']) => {
    const labels = {
      distribution: 'Distribución',
      request: 'Pedido',
      transfer: 'Transferencia',
      return: 'Devolución'
    };
    return labels[type];
  };

  const handleEditItem = (itemId: string, field: string, value: any) => {
    setEditingItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const getItemValue = (itemId: string, field: string, defaultValue: any) => {
    return editingItems[itemId]?.[field] ?? defaultValue;
  };

  // Keypad handlers for cajero
  const openKeypad = (itemId: string, ingredientName: string, currentValue: number, fieldName: string = 'receivedQuantity') => {
    setKeypadItemId(itemId);
    const fieldLabel = fieldName === 'confirmedQuantity' ? 'Cantidad Confirmada' : 'Cantidad Recibida';
    setKeypadTitle(`${fieldLabel} - ${ingredientName}`);
    setKeypadInitialValue(currentValue.toString());
    setKeypadFieldName(fieldName);
    setKeypadOpen(true);
  };

  const handleKeypadSubmit = (value: string) => {
    if (keypadItemId) {
      handleEditItem(keypadItemId, keypadFieldName, parseFloat(value) || 0);
    }
    setKeypadOpen(false);
    setKeypadItemId(null);
  };

  const handleApproveTransfer = () => {
    openConfirmModal(
      'Enviar Transferencia',
      `¿Está seguro que desea enviar la transferencia ${transfer?.transferNumber}?`,
      () => {}, // placeholder, se ejecuta handleConfirmShip
      true
    );
  };

  const handleConfirmShip = async () => {
    try {
      setSaving(true);

      // Preparar items confirmados
      const confirmedItems = transfer?.items.map(item => ({
        id: parseInt(item.id),
        confirmedQuantity: getItemValue(item.id, 'confirmedQuantity', item.confirmedQuantity || item.requestedQuantity)
      })) || [];

      // Llamar a la API para cambiar estado a in_transit
      const response = await authFetch(`${API_BASE_URL}/transfers/${transfer?.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'in_transit',
          confirmedItems,
          shippingData: {
            ...shippingData,
            preparatorId: selectedPreparator
          }
        })
      });

      if (!response.ok) {
        throw new Error('Error al enviar transferencia');
      }

      toast.success('Transferencia enviada a tránsito. Stock descontado del local origen.');
      closeConfirmModal();
      navigate('/distribution');
    } catch (error) {
      console.error('Error sending transfer:', error);
      toast.error('Error al enviar transferencia');
    } finally {
      setSaving(false);
    }
  };

  const handleReceiveTransfer = () => {
    const onConfirm = async () => {
      try {
        setSaving(true);

        // Prepare received items from editingItems or use confirmed quantities
        const receivedItems = transfer?.items.map(item => ({
          id: parseInt(item.id),
          receivedQuantity: getItemValue(item.id, 'receivedQuantity', item.confirmedQuantity || item.requestedQuantity)
        })) || [];

        const response = await authFetch(`${API_BASE_URL}/transfers/${transfer?.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            status: 'received',
            receivedItems
          })
        });

        if (!response.ok) {
          throw new Error('Error al recibir transferencia');
        }

        toast.success('Transferencia marcada como recibida');
        closeConfirmModal();
        navigate('/distribution');
      } catch (error) {
        console.error('Error receiving transfer:', error);
        toast.error('Error al recibir transferencia');
      } finally {
        setSaving(false);
      }
    };

    openConfirmModal(
      'Marcar como Recibida',
      `¿Confirma que ha recibido la transferencia ${transfer?.transferNumber}?`,
      onConfirm
    );
  };

  const handleCancelTransfer = () => {
    const onConfirm = () => {
      console.log('Cancelling transfer:', transfer?.id);
      closeConfirmModal();
      alert('Transferencia cancelada');
      navigate('/distribution');
    };

    openConfirmModal(
      'Cancelar Transferencia',
      `¿Está seguro que desea cancelar la transferencia ${transfer?.transferNumber}?`,
      onConfirm
    );
  };

  const openConfirmModal = (title: string, message: string, onConfirm: () => void, requiresPreparator: boolean = false) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      requiresPreparator
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {},
      requiresPreparator: false
    });
    setSelectedPreparator('');
    setShippingData({
      driverId: '',
      vehicleId: '',
      transportCompanyId: '',
      estimatedDeliveryDate: '',
      estimatedDeliveryTime: '',
      observations: ''
    });
  };

  const updateShippingData = (field: string, value: string) => {
    setShippingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const hasDiscrepancy = (confirmedQuantity: number, receivedQuantity: number) => {
    return confirmedQuantity !== receivedQuantity;
  };

  // Paginación de items
  const paginatedItems = useMemo(() => {
    if (!transfer) return [];
    const start = (currentPage - 1) * itemsPerPage;
    return transfer.items.slice(start, start + itemsPerPage);
  }, [transfer, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    if (!transfer) return 1;
    return Math.ceil(transfer.items.length / itemsPerPage);
  }, [transfer, itemsPerPage]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Cargando detalle de transferencia...</p>
        </div>
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Transferencia no encontrada</h2>
          <Button onClick={() => navigate('/distribution')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Distribución
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/distribution')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Detalle de Transferencia {transfer.transferNumber}
            </h1>
            <p className="text-gray-600 mt-1 text-sm md:text-base">
              {transfer.fromLocation?.name} <ArrowRight className="w-4 h-4 inline" /> {transfer.toLocation?.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {getStatusIcon(transfer.status)}
          {getStatusBadge(transfer.status)}
        </div>
      </div>

      {/* Información General */}
      <Card>
        <CardHeader>
          <CardTitle>Información General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Tipo de Transferencia</p>
              <p className="font-semibold">{getTypeLabel(transfer.type)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Fecha de Creación</p>
              <p className="font-semibold">
                {transfer.requestedAt ? new Date(transfer.requestedAt).toLocaleDateString('es-PE') : '-'}
              </p>
            </div>
            {transfer.status === 'in_transit' && transfer.shippedAt && (
              <div>
                <p className="text-sm text-gray-600">Fecha de Envío</p>
                <p className="font-semibold">
                  {new Date(transfer.shippedAt).toLocaleDateString('es-PE')}
                </p>
              </div>
            )}
            {transfer.status === 'received' && transfer.receivedAt && (
              <div>
                <p className="text-sm text-gray-600">Fecha de Recepción</p>
                <p className="font-semibold">
                  {new Date(transfer.receivedAt).toLocaleDateString('es-PE')}
                </p>
              </div>
            )}
          </div>

          {/* Datos del Transporte - inline en la misma card */}
          {(transfer.status === 'in_transit' || transfer.status === 'received') && transferShippingInfo && (
            transferShippingInfo.driver || transferShippingInfo.vehicle || transferShippingInfo.transportCompany || transferShippingInfo.preparator
          ) && (
            <>
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Datos del Transporte - Guía de Remisión
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {transferShippingInfo.preparator && (
                    <div>
                      <p className="text-sm text-gray-600">Preparador</p>
                      <p className="font-semibold">{transferShippingInfo.preparator.fullName}</p>
                      {transferShippingInfo.preparator.position && (
                        <p className="text-xs text-gray-500">{transferShippingInfo.preparator.position}</p>
                      )}
                    </div>
                  )}
                  {transferShippingInfo.driver && (
                    <div>
                      <p className="text-sm text-gray-600">Chofer</p>
                      <p className="font-semibold">{transferShippingInfo.driver.fullName}</p>
                      <p className="text-xs text-gray-500">Lic: {transferShippingInfo.driver.licenseNumber} ({transferShippingInfo.driver.licenseType})</p>
                    </div>
                  )}
                  {transferShippingInfo.vehicle && (
                    <div>
                      <p className="text-sm text-gray-600">Vehículo</p>
                      <p className="font-semibold">{transferShippingInfo.vehicle.plate}</p>
                      <p className="text-xs text-gray-500">{transferShippingInfo.vehicle.brand} {transferShippingInfo.vehicle.model}</p>
                    </div>
                  )}
                  {transferShippingInfo.transportCompany && (
                    <div>
                      <p className="text-sm text-gray-600">Empresa de Transporte</p>
                      <p className="font-semibold">{transferShippingInfo.transportCompany.companyName}</p>
                      <p className="text-xs text-gray-500">RUC: {transferShippingInfo.transportCompany.ruc}</p>
                    </div>
                  )}
                  {transferShippingInfo.estimatedDelivery && (
                    <div>
                      <p className="text-sm text-gray-600">Entrega Estimada</p>
                      <p className="font-semibold">
                        {new Date(transferShippingInfo.estimatedDelivery).toLocaleDateString('es-PE')} {new Date(transferShippingInfo.estimatedDelivery).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  )}
                  {transferShippingInfo.observations && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">Observaciones</p>
                      <p className="font-semibold">{transferShippingInfo.observations}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Tabla de Items */}
      <Card>
        <CardHeader>
          <CardTitle>Productos de la Transferencia</CardTitle>
          <CardDescription>
            {transfer.items.length} item{transfer.items.length !== 1 ? 's' : ''} en total
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-900 text-sm">Ingrediente</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-900 text-sm">Unidad</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-900 text-sm">Cant. Solicitada</th>
                  {transfer.status === 'pending' && (
                    <th className="text-center px-3 py-2 font-medium text-gray-900 text-sm">Cant. Confirmada</th>
                  )}
                  {transfer.status === 'in_transit' && (
                    <>
                      <th className="text-center px-3 py-2 font-medium text-gray-900 text-sm">Cant. Confirmada</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-900 text-sm bg-yellow-50">Cant. Recibida</th>
                    </>
                  )}
                  {transfer.status === 'received' && (
                    <>
                      <th className="text-center px-3 py-2 font-medium text-gray-900 text-sm">Cant. Confirmada</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-900 text-sm">Cant. Recibida</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900">{item.ingredient.name}</div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">{item.unit}</td>
                    <td className="px-3 py-2 text-center text-sm text-gray-900">{item.requestedQuantity}</td>

                    {/* Estado: Pendiente - Cantidad Confirmada (Editable) */}
                    {transfer.status === 'pending' && (
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center">
                          {isCajero ? (
                            <button
                              type="button"
                              onClick={() => openKeypad(
                                item.id,
                                item.ingredient.name,
                                getItemValue(item.id, 'confirmedQuantity', item.confirmedQuantity || item.requestedQuantity),
                                'confirmedQuantity'
                              )}
                              className="w-20 h-8 text-center text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                            >
                              {getItemValue(item.id, 'confirmedQuantity', item.confirmedQuantity || item.requestedQuantity)}
                            </button>
                          ) : (
                            <Input
                              type="number"
                              value={getItemValue(item.id, 'confirmedQuantity', item.confirmedQuantity || item.requestedQuantity)}
                              onChange={(e) => handleEditItem(item.id, 'confirmedQuantity', parseFloat(e.target.value) || 0)}
                              className="w-20 h-8 text-center text-sm"
                              min="0"
                              step="0.1"
                            />
                          )}
                        </div>
                      </td>
                    )}

                    {/* Estado: En Tránsito - Cantidad Confirmada (Solo lectura) + Cantidad Recibida (Editable) */}
                    {transfer.status === 'in_transit' && (
                      <>
                        <td className="px-3 py-2 text-center text-sm text-gray-900">
                          {item.confirmedQuantity || item.requestedQuantity}
                        </td>
                        <td className="px-3 py-2 text-center bg-yellow-50">
                          <div className="flex justify-center">
                            {isCajero ? (
                              <button
                                type="button"
                                onClick={() => openKeypad(
                                  item.id,
                                  item.ingredient.name,
                                  getItemValue(item.id, 'receivedQuantity', item.confirmedQuantity || item.requestedQuantity)
                                )}
                                className="w-20 h-8 text-center text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                              >
                                {getItemValue(item.id, 'receivedQuantity', item.confirmedQuantity || item.requestedQuantity)}
                              </button>
                            ) : (
                              <Input
                                type="number"
                                value={getItemValue(item.id, 'receivedQuantity', item.confirmedQuantity || item.requestedQuantity)}
                                onChange={(e) => handleEditItem(item.id, 'receivedQuantity', parseFloat(e.target.value) || 0)}
                                className="w-20 h-8 text-center text-sm"
                                min="0"
                                step="0.1"
                              />
                            )}
                          </div>
                        </td>
                      </>
                    )}

                    {/* Estado: Recibido - Ambas cantidades (Solo lectura) */}
                    {transfer.status === 'received' && (
                      <>
                        <td className="px-3 py-2 text-center text-sm text-gray-900">
                          {item.confirmedQuantity || item.requestedQuantity}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-sm font-medium ${
                            hasDiscrepancy(
                              item.confirmedQuantity || item.requestedQuantity,
                              item.receivedQuantity || 0
                            )
                            ? 'text-red-600 bg-red-50 px-2 py-1 rounded'
                            : 'text-gray-900'
                          }`}>
                            {item.receivedQuantity || '-'}
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-3">
              <div className="text-sm text-gray-700">
                Mostrando {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, transfer.items.length)} de {transfer.items.length} items
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={currentPage <= 1}
                  className="h-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium text-gray-700 px-2">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage >= totalPages}
                  className="h-8"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Botones de Acción */}
      <div className="flex justify-end gap-3">
        {transfer.status === 'pending' && (
          <>
            <Button
              variant="outline"
              onClick={handleCancelTransfer}
              className="text-red-600 border-red-300 hover:bg-red-50"
              disabled={saving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleApproveTransfer}
              className="bg-green-600 hover:bg-green-700"
              disabled={saving}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {saving ? 'Enviando...' : 'Enviar'}
            </Button>
          </>
        )}
        {transfer.status === 'in_transit' && (
          <Button
            onClick={handleReceiveTransfer}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={saving}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Recibir'}
          </Button>
        )}
      </div>

      {/* Modal de Confirmación */}
      <Dialog open={confirmModal.isOpen} onOpenChange={closeConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmModal.title}</DialogTitle>
            <DialogDescription>
              {confirmModal.message}
            </DialogDescription>
          </DialogHeader>

          {confirmModal.requiresPreparator && (
            <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
              {/* Preparador */}
              <div className="space-y-2">
                <Label htmlFor="preparator">Preparador de Mercadería *</Label>
                <Select value={selectedPreparator} onValueChange={setSelectedPreparator}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un preparador..." />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={String(employee.id)}>
                        {employee.firstName} {employee.lastName} - {employee.position?.name || 'Sin cargo'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Datos del Transporte */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Datos del Transporte para Guía de Remisión</h4>

                <div className="grid grid-cols-2 gap-4">
                  {/* Chofer */}
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="driverId">Chofer *</Label>
                    <Select value={shippingData.driverId} onValueChange={(value) => updateShippingData('driverId', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un chofer..." />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map((driver) => (
                          <SelectItem key={driver.id} value={String(driver.id)}>
                            {driver.fullName} - Lic: {driver.licenseNumber}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Vehículo */}
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="vehicleId">Vehículo *</Label>
                    <Select value={shippingData.vehicleId} onValueChange={(value) => updateShippingData('vehicleId', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un vehículo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map((vehicle) => (
                          <SelectItem key={vehicle.id} value={String(vehicle.id)}>
                            {vehicle.plate} - {vehicle.brand} {vehicle.model}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Empresa de Transporte */}
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="transportCompanyId">Empresa de Transporte *</Label>
                    <Select value={shippingData.transportCompanyId} onValueChange={(value) => updateShippingData('transportCompanyId', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione una empresa..." />
                      </SelectTrigger>
                      <SelectContent>
                        {transportCompanies.map((company) => (
                          <SelectItem key={company.id} value={String(company.id)}>
                            {company.companyName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Fecha y Hora Estimada */}
                  <div className="space-y-2">
                    <Label htmlFor="estimatedDeliveryDate">Fecha Estimada de Entrega *</Label>
                    <Input
                      id="estimatedDeliveryDate"
                      type="date"
                      value={shippingData.estimatedDeliveryDate}
                      onChange={(e) => updateShippingData('estimatedDeliveryDate', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimatedDeliveryTime">Hora Estimada *</Label>
                    <Input
                      id="estimatedDeliveryTime"
                      type="time"
                      value={shippingData.estimatedDeliveryTime}
                      onChange={(e) => updateShippingData('estimatedDeliveryTime', e.target.value)}
                    />
                  </div>

                  {/* Observaciones */}
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="observations">Observaciones</Label>
                    <Textarea
                      id="observations"
                      value={shippingData.observations}
                      onChange={(e) => updateShippingData('observations', e.target.value)}
                      placeholder="Observaciones adicionales para el transporte..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeConfirmModal}>
              Cancelar
            </Button>
            <Button
              onClick={confirmModal.requiresPreparator ? handleConfirmShip : confirmModal.onConfirm}
              disabled={
                saving || (
                  confirmModal.requiresPreparator && (
                    !selectedPreparator ||
                    !shippingData.driverId ||
                    !shippingData.vehicleId ||
                    !shippingData.transportCompanyId ||
                    !shippingData.estimatedDeliveryDate ||
                    !shippingData.estimatedDeliveryTime
                  )
                )
              }
            >
              {saving ? 'Enviando...' : (confirmModal.requiresPreparator ? 'Generar Guía y Enviar' : 'Confirmar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DecimalKeypad for cajero */}
      <DecimalKeypad
        isOpen={keypadOpen}
        onClose={() => setKeypadOpen(false)}
        onSubmit={handleKeypadSubmit}
        title={keypadTitle}
        initialValue={keypadInitialValue}
      />
    </div>
  );
};
